import { date } from '../../format'
import type { VarianceRow } from '../../saber.types'

// §7 — invoice boundary markers for the Daily Production chart, derived from
// the variance rows' billing windows. Daily shadow-billing series periods are
// YYYY-MM-DD — the same format as billingStartDate/billingEndDate — so the
// mapping is an identity pass-through; the chart drops markers whose period
// does not appear in the charted window.

export type InvoiceMarkerKind = 'start' | 'end'

export interface InvoiceMarker {
  /** Matches the shadow-billing series `period` (daily = YYYY-MM-DD). */
  period: string
  kind: InvoiceMarkerKind
  label: string
  /** Billing window of the invoice(s) at this boundary — for click-to-scope (WEB-6572). */
  from: string
  to: string
}

interface MarkerBucket {
  period: string
  kind: InvoiceMarkerKind
  tenants: Set<string>
  /** Opposite-boundary dates of every invoice sharing this boundary. */
  starts: string[]
  ends: string[]
}

/**
 * Map variance rows to chronological start/end markers. Boundaries shared by
 * multiple invoices are deduped into a single marker whose label names the
 * tenant when unique, else the invoice count.
 */
export const deriveInvoiceMarkers = (rows: VarianceRow[]): InvoiceMarker[] => {
  const buckets = new Map<string, MarkerBucket>()
  for (const row of rows) {
    const start = row.billingStartDate
    const end = row.billingEndDate
    const bounds: Array<{ period: string | null | undefined; kind: InvoiceMarkerKind }> = [
      { period: start, kind: 'start' },
      { period: end, kind: 'end' }
    ]
    for (const { period, kind } of bounds) {
      if (period == null || period === '') continue
      const key = `${period}::${kind}`
      const bucket = buckets.get(key) ?? { period, kind, tenants: new Set<string>(), starts: [], ends: [] }
      bucket.tenants.add(row.tenantName)
      if (start != null && start !== '') bucket.starts.push(start)
      if (end != null && end !== '') bucket.ends.push(end)
      buckets.set(key, bucket)
    }
  }
  return [...buckets.values()]
    .sort((a, b) => (a.period === b.period ? a.kind.localeCompare(b.kind) : a.period.localeCompare(b.period)))
    .map((bucket) => ({
      period: bucket.period,
      kind: bucket.kind,
      label: `Invoice ${bucket.kind} · ${date(bucket.period)} · ${
        bucket.tenants.size === 1 ? [...bucket.tenants][0] : `${bucket.tenants.size} invoices`
      }`,
      // A start marker scopes from its date to the latest end among invoices that
      // start there; an end marker scopes from the earliest matching start.
      from: bucket.kind === 'start' ? bucket.period : bucket.starts.reduce((a, b) => (a < b ? a : b), bucket.period),
      to: bucket.kind === 'end' ? bucket.period : bucket.ends.reduce((a, b) => (a > b ? a : b), bucket.period)
    }))
}
